<script setup>

import {computed, nextTick, onBeforeUnmount, onMounted, reactive, ref} from 'vue'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import AutoComplete from 'primevue/autocomplete'
import {maxDate} from "../helpers/entry_validation";
import {recipientCandidates} from "../functions/form_options";
import Loading from "vue-loading-overlay";
import axios from "axios";
import Swal from 'sweetalert2';
import {useVuelidate} from '@vuelidate/core'
import {required, helpers, requiredIf, and} from '@vuelidate/validators'
import {rentalRoomEntryFormRules} from "../helpers/rental_room_entry_validation";
import dayjs from 'dayjs'
import 'stylesheets/primevue_theme.css' // オリジナルをテーマをコピーして編集したもの
import RentalRoomEntryEditConfirmDialog from "./RentalRoomEntryEditConfirmDialog.vue";

const props = defineProps({
  entry: Object,
  rooms: Array,
  workers: Array,
  designationTypes: Array,
  rentalServices: Array,
  currentUserId: Number
})

const uAkasakaId = props.rooms.find(x => x.name === 'U akasaka').id
const isDisplayedAll = ref(false)
const postModifiedData = reactive({})

const initPeriodDate = (startAt) => {
  if (startAt.format('H') >= 0 && startAt.format('H') < 8)
    startAt = startAt.subtract(1, 'd')
  const displayedDate = startAt.format('YYYY/MM/DD')
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  return `${displayedDate}(${days[startAt.format('d')]})`
}

const formatStartAt = (start_at) => {
  const hour = Number(dayjs(start_at).format('H'))
  return hour >= 0 && hour <= 7 ? hour + 24 : hour
}

const formatEndAt = (end_at) => {
  const hour = Number(dayjs(end_at).format('H'))
  return hour >= 0 && hour <= 8 ? hour + 24 : hour
}

const isPast = (start_at) => {
  return dayjs(start_at) <= dayjs()
}

const hasCancellationFee = (start_at) => {
  return !isPast(start_at) && dayjs(start_at).subtract(1, 'd').startOf('d').add(18, 'h') <= dayjs()
}

const isDisabled = (period) => {
  return period.isCanceled || period.isPast || period.hasCancellationFee
}

const isAlreadyDesignated = (entry) => {
  return entry.worker_ids.length !== 0 || entry.reservations.some((r) =>  !['adjusting', null].includes(r?.worker_status))
}

const formData = reactive({
  visible: false,
  confirmVisible: false,
  entry: {
    id: props.entry.entry_id,
    name: props.entry.name,
    periods: props.entry.reservations.map((reservation) => {
      return{
        isCanceled: reservation.canceled_at !== null,
        hasCancellationFee: hasCancellationFee(reservation.start_at),
        isPast: isPast(reservation.start_at),
        id: reservation.id,
        date: initPeriodDate(dayjs(reservation.start_at)),
        startHour: formatStartAt(reservation.start_at),
        startMinute: String(dayjs(reservation.start_at).format('mm')),
        endHour: formatEndAt(reservation.end_at),
        endMinute: dayjs(reservation.end_at).format('mm'),
        rentalRoomId: uAkasakaId, uuid: crypto.randomUUID(), isReserved: false, isReservedMyself: false
      }
    }),
    needsInsideWorker: props.entry.needs_inside_worker,
    isAlreadyDesignated: isAlreadyDesignated(props.entry),
    firstDesignationUser: props.entry.worker_ids[0],
    firstDesignationType: props.entry.designation_type_ids[0],
    secondDesignationUser: props.entry.worker_ids[1],
    secondDesignationType: props.entry.designation_type_ids[1],
    hasOutsideWorker: props.entry.has_outside_worker,
    note: props.entry.note,
    rentalServiceIds: props.entry.service_ids,
    duplicatedReservationIds: [],
    recipients: props.entry.recipients,
    signage_company: props.entry.signage_company,
    signage_person: props.entry.signage_person,
    signage_person_suffix: props.entry.signage_person_suffix
  }
})

const v$ = useVuelidate(rentalRoomEntryFormRules, formData)

const loading = ref(false)

const formatDate = (date) => {
  const days = ["日", "月", "火", "水", "木", "金", "土"];

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = date.getDay();
  return `${year}/${month}/${day}(${days[dayOfWeek]})`
}

const addPeriod = () => {
  // TODO: U akasaka以外の部屋が増えたら要見直し
  formData.entry.periods.push({
    date: null, startHour: 10, startMinute: '00', endHour: 32, endMinute: '00', rentalRoomId: uAkasakaId,
    uuid: crypto.randomUUID(), isReserved: false, isReservedMyself: false
  })
}

const removePeriod = (index) => {
  formData.entry.periods.splice(index, 1)
}
const showForm = () => {
  window.close()
  formData.visible = true
}
const hideForm = () => {
  formData.visible = false
}

const needsInsideWorkerChanged = () => {
  if (formData.entry.needsInsideWorker) return
  formData.entry.firstDesignationUser = null
  formData.entry.firstDesignationType = null
  formData.entry.secondDesignationUser = null
  formData.entry.secondDesignationType = null
}

const firstDesignationUserChanged = () => {
  v$.value.entry.firstDesignationType.$touch()
  if (formData.entry.firstDesignationUser) return
  formData.entry.firstDesignationType = null
}

const secondDesignationUserChanged = () => {
  v$.value.entry.secondDesignationType.$touch()
  if (formData.entry.secondDesignationUser) return
  formData.entry.secondDesignationType = null
}

const recipientCandidatesResult = ref([]) // インポートしているメソッド名と被るためresultにしてます
const filteredRecipientCandidates = ref([])
const searchRecipientCandidates = (event) => {
  setTimeout(() => {
    if (!event.query.trim().length) {
      filteredRecipientCandidates.value = [...recipientCandidatesResult.value];
    } else {
      filteredRecipientCandidates.value = recipientCandidatesResult.value.filter((user) => {
        return user.name.toLowerCase().includes(event.query.toLowerCase())
      });
    }
  }, 250);
}


const showErrorMessage = ref(false)
const errorMessage = ref('')
const closeErrorMessage = () => {
  showErrorMessage.value = false
}

const errorMessageText = computed(() => {
  return errorMessage.value.rental_room_reservations?.map(r => {
    const dateText = dayjs(r.start_at).format('YYYY/MM/DD HH:mm')
    return `${dateText} ${r.messages.join(', ')}`;
  }).join(', ')
})

const openConfirmMessage = () => {
  formData.confirmVisible = true
}

const hideConfirmMessage = () => {
  formData.confirmVisible = false
}

const hasMyselfReservations = ref(false)
const hasDuplicatedReservations = ref(false)

const putFormData = computed(() => {
  return Object.assign(
      formData.entry,
      {
        modified_data: [],
        rental_room_reservations: formData.entry.periods.map(p => {
          // キャンセル済み or 過去の予約はスキップ
          if (!(p.isPast || p.hasCancellationFee || p.isCanceled)) {
            return {
              id: p.id,
              rental_room_id: p.rentalRoomId,
              start_at: dayjs(p.date)
                  .startOf('date')
                  .add(p.startHour, 'hours')
                  .add(p.startMinute, 'minutes').format(),
              end_at: dayjs(p.date)
                  .startOf('date')
                  .add(p.endHour, 'hours')
                  .add(p.endMinute, 'minutes').format(),
              uuid: p.uuid,
              is_reserved: false,
              is_reserved_myself: false,
              modified_data: []
            }
          }
        }).filter(v => v)
      }
  )
})

const initReservationStatus = () => {
  hasDuplicatedReservations.value = false
  hasMyselfReservations.value = false
  formData.entry.periods.forEach((period) => period.isReserved = false)
  formData.entry.periods.forEach((period) => period.isReservedMyself = false)
}

const identifyModifiedEntry = async () => {
  try {
    loading.value = true
    initReservationStatus()
    postModifiedData.value = await axios.post('/reservation_modifications', putFormData.value)
    postModifiedData.value.data.rental_room_reservations.forEach((reservation) => {
      if (reservation.is_reserved) {
        hasDuplicatedReservations.value = true
        formData.entry.periods.find((period) => period.uuid === reservation.uuid).isReserved = true
      }
      if (reservation.is_reserved_myself) {
        hasMyselfReservations.value = true
        formData.entry.periods.find((period) => period.uuid === reservation.uuid).isReservedMyself = true
      }
    })
    loading.value = false
    openConfirmMessage()

  } catch (error) {
    showErrorMessage.value = true
    errorMessage.value = error.response?.data
    if (errorMessage.value?.error) errorMessage.value = errorMessage.value.error
    // 通信エラー時のメッセージ
    if (errorMessage.value?.base) errorMessage.value = errorMessage.value.base.join(' , ')
    loading.value = false
  }
}

const changeFromConfirmDialog = (attr_name, e) => {
  formData.entry[attr_name] = e.target.value
  postModifiedData.value.data[attr_name] = e.target.value
}

const submit = async () => {
  hideConfirmMessage()
  try {
    loading.value = true
    await axios.put(`/rental_room_entries/${postModifiedData.value.data.id}`, postModifiedData.value.data)
    loading.value = false
    hideForm()
    const modal = await Swal.fire({
      icon: 'success',
      title: '予約を更新しました',
      text: '数分以内に確認メールが送信されます',
      confirmButtonText: '閉じる',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    if (modal.isConfirmed) window.location.reload()

  } catch (error) {
    showErrorMessage.value = true
    console.log(error)
    errorMessage.value = error.response.data
    if (errorMessage.value?.error) errorMessage.value = errorMessage.value.error
    // 通信エラー時のメッセージ
    if (errorMessage.value?.base) errorMessage.value = errorMessage.value.base.join(' , ')
    loading.value = false
  }
}

const windowWidth = ref(1000)
const onResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(async () => {
  nextTick(() => {
    window.addEventListener('resize', onResize);
    onResize()
  });
  recipientCandidatesResult.value = await recipientCandidates(props.currentUserId)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
})

const hours8to32 = [...Array(33).keys()].slice(8)
const minutes0to30 = ['00', '30']
const displayReservation = () => isDisplayedAll.value = !isDisplayedAll.value
</script>

<template>
  <Loading :active="loading" :is-full-page="true"/>


  <button class="mt-2 btn btn-outline btn-sm" @click="showForm">
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-1 h-4 w-4">
      <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
    予約変更
  </button>
  <Dialog v-model:visible="formData.visible" modal header="予約情報を入力してください"
          :style="{ width: windowWidth > 768 ? '70vw':'100vw' }">

    <div class="flex flex-col gap-8 overflow-y-auto max-h-[80vh]">
      <div v-if="showErrorMessage" class="sticky top-0 z-50">
        <!--登録失敗-->
        <Message severity="error" @close="closeErrorMessage">
          <div>登録に失敗しました。</div>
          <div v-if="errorMessageText">詳細：{{ errorMessageText }}</div>
        </Message>
      </div>

      <!--施設ご利用料金-->
      <div class="rounded-md bg-gray-200 p-4">
        <div class="mb-2 text-base font-semibold leading-7 text-gray-900">施設ご利用料金</div>
        <div class="text-sm font-light text-gray-600">
          <div>・１時間あたり7,000円</div>
          <div>・１日上限料金50,000円（１日＝８時〜翌朝８時）</div>
          <div class="mt-1">※弊社エディター人件費、IRCAご利用料金は別途申し受けます。
          </div>
        </div>
      </div>

      <!--案件名-->
      <div class="flex flex-col gap-2">
        <div class="text-base font-semibold leading-7 text">
          <span class="text-base font-semibold leading-7 text-gray-900"><span
              class="text-red-600">【必須】</span>案件名</span>
          <span class="ml-2 text-sm">※御見積書・御請求書に記載されます</span>
        </div>
        <InputText type="text" v-model="formData.entry.name" class="w-full" :size="windowWidth > 768 ? null : 'small'"
                   @blur="v$.entry.name.$touch"></InputText>
        <span v-if="v$.entry.name.$errors">
          <span id="name-error" v-for="(error, index) of v$.entry.name.$errors" :key="index">
            <small class="p-error">{{ error.$message }}</small>
          </span>
        </span>
      </div>

      <!--予約日時 md-->
      <div class="hidden flex-col md:flex">
        <div class="flex items-center">
          <span class="text-base font-semibold leading-7 text-gray-900">予約日時</span>
          <span class="ml-6">
            <Button size="small" severity="secondary" @click="displayReservation"
                    :icon="isDisplayedAll ? 'pi pi-angle-up' :'pi pi-angle-down'"
                    outlined :label="isDisplayedAll ? 'キャンセル済み・過去の予約を非表示' : 'キャンセル済み・過去の予約を表示'"/>
          </span>
        </div>
        <div v-for="(period, periodIndex) in formData.entry.periods" class="ml-6">
          <div v-if="isDisplayedAll || !isDisabled(period)" class="my-4">
            <div class="flex items-end gap-8">
              <div>
                <!-- 希望日-->
                <div v-if="period.id" class="mb-3 w-36 pl-2 text-lg">
                  <label class="block text-sm font-medium leading-6 text-gray-900">
                    <span v-if="period.isReserved" class="mb-2 text-red-500 badge badge-sm badge-outline">キャンセル待ち申し込み</span>
                    <span v-if="period.isReservedMyself" class="mb-2 text-red-500 badge badge-sm badge-outline">既予約日</span>
                    <span v-if="period.isCanceled" class="mb-2 text-gray-600 badge badge-sm badge-outline">キャンセル済み</span>
                    <span v-else-if="period.past || period.hasCancellationFee" class="mb-2 text-gray-600 badge badge-sm badge-outline">変更不可</span>
                  </label>
                  {{ period.date }}
                </div>
                <div v-else>
                  <Calendar v-model="period.date" dateFormat="yy/m/d(D)" placeholder="希望日"
                            :min-date="new Date()"
                            :max-date="maxDate(2)" @blur="v$.entry.periods.$touch"
                            :pt="{ input: { class: isDisabled(period) ? 'bg-gray-200 w-36' : 'w-36' } }"
                            :disabled="isDisabled(period)"/>
                </div>
              </div>
              <div class="mr-8 flex">
                <div class="flex items-center">
                  <Dropdown v-model="period.startHour" :options="hours8to32" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']" unstyled
                            :disabled="isDisabled(period)"/>
                  <div class="mx-1">:</div>
                  <Dropdown v-model="period.startMinute" :options="minutes0to30" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']"
                            :disabled="isDisabled(period)"/>
                  <div class="mx-2">〜</div>
                  <Dropdown v-model="period.endHour" :options="hours8to32" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']"
                            :disabled="isDisabled(period)"/>
                  <div class="mx-1">:</div>
                  <Dropdown v-model="period.endMinute" :options="minutes0to30" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']"
                            :disabled="isDisabled(period)"/>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium leading-6 text-gray-900">会議室名</label>
                <Dropdown v-model="period.rentalRoomId" :options="rooms" optionLabel="name" option-value="id"
                          :class="isDisabled(period) ? 'bg-gray-200' : ''" :disabled="isDisabled(period)"/>
              </div>
              <div v-if="!(isDisabled(period))">
                <Button icon="pi pi-times" outlined severity="danger" @click="removePeriod(periodIndex)"></Button>
              </div>
            </div>
            <div v-if="period.hasCancellationFee" class="mt-2 text-sm text-gray-600">※前日18時以降のキャンセルは各日程から申し込みをおこなってください。</div>
            <div v-if="v$.entry.periods.$each.$message[periodIndex]" class="text-sm p-error">
              {{ v$.entry.periods.$each.$message[periodIndex].join(', ') }}
            </div>
          </div>
        </div>

        <div class="mt-2 ml-6">
          <Button icon="pi pi-plus" severity="secondary" outlined label="日程追加" @click="addPeriod"/>
        </div>
        <div class="mt-2 ml-6 text-sm">
          <span class="mb-1 font-semibold text-red-600">※完全撤収可能な余裕を持ったお時間をご入力下さい。</span>
          <span class="text-gray-600">後続の予約が入る可能性がございます。</span>
        </div>
        <div class="ml-6 text-sm">
          <span class="text-gray-600">日付を変更したい場合は、予約を削除して日程追加をおこなってください</span>
        </div>
      </div>

      <!--予約日時 xs-->
      <div class="flex-col md:hidden">
        <div class="flex items-center">
          <span class="text-base font-semibold leading-7 text-gray-900">予約日時</span>
          <span class="ml-6">
            <Button size="small" severity="secondary" @click="displayReservation"
                    :icon="isDisplayedAll ? 'pi pi-angle-up' :'pi pi-angle-down'"
                    outlined :label="isDisplayedAll ? 'キャンセル済み・過去の予約を非表示' : 'キャンセル済み・過去の予約を表示'"/>
          </span>
        </div>

        <div v-for="(period, periodIndex) in formData.entry.periods">
          <div v-if="isDisplayedAll || !isDisabled(period)" class="mt-2 flex-col flex-wrap items-end rounded-md border border-gray-300 p-6">
            <div>
              <!-- 希望日-->
              <div v-if="period.id" class="mb-3 w-36 pl-2 text-lg">
                <label class="block text-sm font-medium leading-6 text-gray-900">
                  <span v-if="period.isReserved" class="mb-2 text-red-500 badge badge-sm badge-outline">キャンセル待ち申し込み</span>
                  <span v-if="period.isReservedMyself" class="mb-2 text-red-500 badge badge-sm badge-outline">既予約日</span>
                  <span v-if="period.isCanceled" class="mb-2 text-gray-600 badge badge-sm badge-outline">キャンセル済み</span>
                  <span v-else-if="period.past || period.hasCancellationFee" class="mb-2 text-gray-600 badge badge-sm badge-outline">変更不可</span>
                </label>
                {{ period.date }}
              </div>
              <div v-else>
                <Calendar v-model="period.date" dateFormat="yy/m/d(D)" placeholder="希望日"
                          :min-date="new Date()"
                          :max-date="maxDate(2)" @blur="v$.entry.periods.$touch"
                          :pt="{ input: { class: isDisabled(period) ? 'bg-gray-200 w-36' : 'w-36' } }"
                          :disabled="isDisabled(period)"/>
              </div>
            </div>
            <div class="mt-4 mr-8 flex items-center">
              <Dropdown v-model="period.startHour" :options="hours8to32" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'fill-gray-200 custom-date-dropdown']"
                        :disabled="isDisabled(period)"/>
              <div class="mx-1">:</div>
              <Dropdown v-model="period.startMinute" :options="minutes0to30" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']"
                        :disabled="isDisabled(period)"/>
            </div>
            <div class="mt-2 flex items-center">
              <div class="mr-2 ml-4">〜</div>
              <Dropdown v-model="period.endHour" :options="hours8to32" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']"
                        :disabled="isDisabled(period)"/>
              <div class="mx-1">:</div>
              <Dropdown v-model="period.endMinute" :options="minutes0to30" :class="[isDisabled(period) ? 'bg-gray-200' : '', 'custom-date-dropdown']"
                        :disabled="isDisabled(period)"/>
            </div>
            <div class="mt-4">
              <label class="block text-sm font-medium leading-6 text-gray-900">会議室名</label>
              <Dropdown v-model="period.rentalRoomId" :options="rooms" optionLabel="name" option-value="id" class="rooms"
                        :class="isDisabled(period) ? 'bg-gray-200' : ''" :disabled="isDisabled(period)"/>
            </div>
            <div v-if="v$.entry.periods.$each.$message[periodIndex]" class="text-sm p-error">
              {{ v$.entry.periods.$each.$message[periodIndex].join(', ') }}
            </div>
            <div v-if="!(isDisabled(period))">
              <Button icon="pi pi-times" outlined severity="danger" @click="removePeriod(periodIndex)" class="remove-period"></Button>
            </div>
          </div>
        </div>

        <div class="my-4">
          <Button icon="pi pi-plus" severity="secondary" outlined label="日程追加" @click="addPeriod"/>
        </div>
        <div class="ml-6 text-sm">
          <span class="mb-1 font-semibold text-red-600">※完全撤収可能な余裕を持ったお時間をご入力下さい。</span>
          <span class="text-gray-600">後続の予約が入る可能性がございます。</span>
        </div>
        <div class="ml-6 text-sm">
          <span class="text-gray-600">日付を変更したい場合は、予約を削除して日程追加をおこなってください</span>
        </div>
      </div>

      <!--エディター-->
      <div>
        <div class="mb-2 text-base font-semibold leading-7 text-gray-900">エディター</div>
        <div class="ml-6">
          <div class="my-6 flex items-center gap-2">
            <Checkbox v-model="formData.entry.needsInsideWorker" inputId="needsInsideWorker" :binary="true"
                      @change="needsInsideWorkerChanged" :disabled="formData.entry.isAlreadyDesignated"/>
            <label for="needsInsideWorker">
              <span class="mb-4 text-sm text-gray-600">
                弊社エディターを希望（<span class="font-semibold text-red-600">※要調整</span>）
              </span>
            </label>
          </div>
          <div class="my-6 ml-6" v-if="formData.entry.needsInsideWorker">
            <div class="mb-6 flex flex-col gap-4 md:flex-row">
              <div class="col-span-1 flex items-center gap-4">
                <label class="text-sm md:text-base">第一希望</label>
                <div class="flex flex-col">
                  <Dropdown v-model="formData.entry.firstDesignationUser" :options="workers" optionLabel="name"
                            option-value="id" @change="firstDesignationUserChanged" :disabled="formData.entry.isAlreadyDesignated"
                            :class="formData.entry.isAlreadyDesignated ? 'bg-gray-200' : '', 'custom-date-dropdown'"/>

                </div>
              </div>
              <div class="col-span-1 flex items-center gap-4">
                <label class="text-sm md:text-base">指名理由</label>
                <div class="flex flex-col">
                  <Dropdown v-model="formData.entry.firstDesignationType" :options="designationTypes" optionLabel="name"
                            option-value="id" :disabled="formData.entry.isAlreadyDesignated"
                            :class="formData.entry.isAlreadyDesignated ? 'bg-gray-200' : '', 'custom-date-dropdown'"/>
                  <div>
                    <span v-if="v$.entry.firstDesignationType.$errors" class="text-sm p-error">
                      {{ v$.entry.firstDesignationType.$errors.map(e => e.$message).join(', ') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="mb-6 flex flex-col gap-4 md:flex-row" v-if="formData.entry.needsInsideWorker">
              <div class="col-span-1 flex items-center gap-4">
                <label class="text-sm md:text-base">第二希望</label>
                <div class="flex flex-col">
                  <Dropdown v-model="formData.entry.secondDesignationUser" :options="workers" optionLabel="name"
                            option-value="id" @blur="v$.entry.secondDesignationUser.$touch"
                            @change="secondDesignationUserChanged" :disabled="formData.entry.isAlreadyDesignated"
                            :class="formData.entry.isAlreadyDesignated ? 'bg-gray-200' : '', 'custom-date-dropdown'"/>
                  <div>
                    <span v-if="v$.entry.secondDesignationUser.$errors" class="text-sm p-error">
                      {{ v$.entry.secondDesignationUser.$errors.map(e => e.$message).join(', ') }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-span-1 flex items-center gap-4">
                <label class="text-sm md:text-base">指名理由</label>
                <div class="flex flex-col">
                  <Dropdown v-model="formData.entry.secondDesignationType" :options="designationTypes"
                            optionLabel="name" option-value="id" :disabled="formData.entry.isAlreadyDesignated"
                            :class="formData.entry.isAlreadyDesignated ? 'bg-gray-200' : '', 'custom-date-dropdown'"/>
                  <div>
                    <span v-if="v$.entry.secondDesignationType.$errors" class="text-sm p-error">
                      {{ v$.entry.secondDesignationType.$errors.map(e => e.$message).join(', ') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-span-2 text-sm text-gray-600">
              ※調整が出来次第ご連絡いたします。ご要望にお応えできない場合もございます。
            </div>
            <div class="col-span-2 text-sm text-gray-600">
              ※一度「登録」をしたあとはSMART BOOING上でのスタッフ変更はできません。変更をご希望の場合は、03-3542-1748 もしくは担当営業までご連絡ください。
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="formData.entry.hasOutsideWorker" inputId="hasOutsideWorker" :binary="true"/>
            <label for="hasOutsideWorker">
              <span class="mb-4 text-sm text-gray-600">
                外部スタッフが作業（お客様ご手配）
              </span>
            </label>
          </div>
          <div class="my-4 ml-6 text-sm font-semibold text-red-600">
            ※編集機材のご手配をお願いいたします。機材貸し出しは行っておりません。
          </div>

        </div>
      </div>
      <!--備考-->
      <div class="flex flex-col gap-2">
        <div class="flex flex-row items-center">
          <div class="text-base font-semibold leading-7 text-gray-900">備考</div>
        </div>
        <Textarea v-model="formData.entry.note"/>
      </div>
      <!--オプションサービス-->
      <div class="flex flex-col gap-2">
        <div class="flex flex-row items-center">
          <div class="text-base font-semibold leading-7 text-gray-900">オプションサービス</div>
        </div>
        <div v-for="rentalService in rentalServices" :key="rentalService.id">
          <div class="flex lign-items-center">
            <Checkbox v-model="formData.entry.rentalServiceIds" name="rentalService" :value="rentalService.id"/>
            <label :for="rentalService.id">{{ rentalService.name }}</label>
          </div>
          <div class="flex gap-8 pl-8 text-gray-500">
            <div>{{ rentalService.fee }}</div>
            <div>{{ rentalService.note }}</div>
          </div>
        </div>
        <!--   通知先   -->
        <div class="flex flex-col gap-2">
          <div class="flex flex-row items-center">
            <div class="text-base font-semibold leading-7 text-gray-900">通知先</div>
          </div>
          <div>
          <span class="p-float-label">
            <AutoComplete id="production-users" v-model="formData.entry.recipients"
                          :suggestions="filteredRecipientCandidates"
                          @complete="searchRecipientCandidates($event)" :dropdown="true" :multiple="true" field="name"
                          placeholder="ユーザー検索" scrollHeight="400px" forceSelection/>
          </span>
          </div>
        </div>
      </div>

    </div>
    <template #footer>
      <Button severity="secondary" outlined label="閉じる" @click="hideForm"/>
      <Button severity="success" label="確認" @click="identifyModifiedEntry" class="ml-4" :disabled="v$.$invalid"/>
    </template>
  </Dialog>
  <RentalRoomEntryEditConfirmDialog :form-data="formData" :has-duplicated-reservations="hasDuplicatedReservations"
                                :has-myself-reservations="hasMyselfReservations" :window-width="windowWidth"
                                :rooms="rooms" :workers="workers" :designation-types="designationTypes"
                                :rental-services="rentalServices" :post-modified-data="postModifiedData"
                                @submit="submit" @hideConfirmMessage="hideConfirmMessage" @change="changeFromConfirmDialog"/>
</template>

<style lang="scss" scoped>
.custom-date-dropdown {
  width: 5.1rem;
}

@media (max-width: 768px){
  :deep.custom-date-dropdown{
    width: 4rem;
    span{
      // text-xs
      font-size: 0.75rem;
      line-height: 1rem;
      // p-2
      padding: 0.5rem;
    }
    div{
      width: 1rem;
    }
  }
  :deep input{
    // text-xs
    font-size: 0.75rem;
    line-height: 1rem;
    // p-2
    padding: 0.5rem;
  }
  :deep .remove-period{
    // text-xs
    font-size: 0.75rem;
    line-height: 1rem;
    // p-2
    padding: 0.5rem;
  }
  :deep .rooms{
    span{
      // text-xs
      font-size: 0.75rem;
      line-height: 1rem;
      // p-2
      padding: 0.5rem;
    }
    div{
      width: 1rem;
    }
  }
}


</style>